import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { eventFinancesApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { EventDetail } from "../../types";
import { formatINR } from "../../utils/format";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { CurrencyInput } from "../CurrencyInput";

type Props = {
  workspace: WorkspaceId;
  eventId: string;
  finances: EventDetail["finances"];
};

/**
 * Production / Marketing / Artist fee editor.
 *
 * Sprint 14 moved this off the Overview tab, which the PDF asks to keep
 * uncluttered, onto Budget & Costs where the rest of the cost editing lives.
 */
export const EventCostsPanel: React.FC<Props> = ({ workspace, eventId, finances }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    production_cost: "",
    marketing_cost: "",
    artist_fee: "",
  });

  const mutation = useMutation({
    mutationFn: async (payload: {
      production_cost: number;
      marketing_cost: number;
      artist_fee: number;
    }) => {
      if (!token) throw new Error("Missing event context");
      return eventFinancesApi.update(eventId, payload, token);
    },
    onSuccess: async () => {
      toast.success("Event costs updated");
      setEditing(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.events.dashboard(workspace, eventId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.events.all(workspace) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.eventOs.all(workspace) }),
      ]);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not update finances");
    },
  });

  const beginEdit = () => {
    setDraft({
      production_cost: String(finances?.production_cost ?? ""),
      marketing_cost: String(finances?.marketing_cost ?? ""),
      artist_fee: String(finances?.artist_fee ?? ""),
    });
    setEditing(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const prod = Number(draft.production_cost);
    const mkt = Number(draft.marketing_cost);
    const fee = Number(draft.artist_fee);
    if (
      !Number.isFinite(prod) ||
      prod < 0 ||
      !Number.isFinite(mkt) ||
      mkt < 0 ||
      !Number.isFinite(fee) ||
      fee < 0
    ) {
      toast.error("Enter non-negative numbers for all costs");
      return;
    }
    mutation.mutate({ production_cost: prod, marketing_cost: mkt, artist_fee: fee });
  };

  return (
    <div className="eventos-card">
      <div className="app-shell-dash-panel-head">
        <div>
          <div className="eventos-label">Event costs</div>
          <div className="eventos-subtle">
            Production · Marketing · Artist fee. Editing recalculates projected profit and break-even.
          </div>
        </div>
        {!editing ? (
          <button type="button" className="eventos-nav-btn" onClick={beginEdit}>
            Edit costs
          </button>
        ) : null}
      </div>
      {editing ? (
        <form onSubmit={submit} className="eventos-form">
          <div className="eventos-form-grid">
            <label className="eventos-field">
              <span className="eventos-field-label">Production cost</span>
              <CurrencyInput
                value={draft.production_cost}
                onChange={(e) => setDraft((prev) => ({ ...prev, production_cost: e.target.value }))}
                required
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Marketing cost</span>
              <CurrencyInput
                value={draft.marketing_cost}
                onChange={(e) => setDraft((prev) => ({ ...prev, marketing_cost: e.target.value }))}
                required
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Artist fee</span>
              <CurrencyInput
                value={draft.artist_fee}
                onChange={(e) => setDraft((prev) => ({ ...prev, artist_fee: e.target.value }))}
                required
              />
            </label>
          </div>
          <div className="app-shell-table-actions">
            <button type="submit" className="eventos-primary-btn" disabled={mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save costs"}
            </button>
            <button
              type="button"
              className="eventos-nav-btn"
              onClick={() => setEditing(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="app-shell-kpi-strip">
          <div className="app-shell-kpi">
            <span className="app-shell-kpi-label">Production</span>
            <span className="app-shell-kpi-value">{formatINR(finances?.production_cost ?? 0)}</span>
          </div>
          <div className="app-shell-kpi">
            <span className="app-shell-kpi-label">Marketing</span>
            <span className="app-shell-kpi-value">{formatINR(finances?.marketing_cost ?? 0)}</span>
          </div>
          <div className="app-shell-kpi">
            <span className="app-shell-kpi-label">Artist fee</span>
            <span className="app-shell-kpi-value">{formatINR(finances?.artist_fee ?? 0)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventCostsPanel;
